/**
 * JE line amount resolution
 *
 * One place decides what a journal-entry line is worth, so the exported detail
 * rows and the rows handed to validation can never disagree.
 *
 * The important distinction here is between a line that is *worth zero* and a
 * line that has *no usable amount at all*: the first is noise MV doesn't need
 * (payroll exports are full of $0 FUTA/SUI/benefit lines), the second is a data
 * problem — a mis-mapped column, a stray text cell — and must stay visible.
 * `resolveLineAmount` returns 0 for the first and null for the second.
 */
import { coerceNumber } from './ingest';

/**
 * Signed amount for a JE line, or null when the row carries no usable amount.
 *
 * Prefers a single signed `amount` column; falls back to debit-positive /
 * credit-negative. Text amounts ($1,000, (500), 1000-) are coerced.
 */
export function resolveLineAmount(row) {
  const amount = coerceNumber(row.amount);
  if (amount !== null) return amount;

  const debit = coerceNumber(row.debit);
  const credit = coerceNumber(row.credit);
  if (debit === null && credit === null) return null;

  return (debit ?? 0) > 0 ? debit : (credit ?? 0) > 0 ? -credit : 0;
}

/**
 * Split lines into the ones worth keeping and a count of the $0 ones dropped.
 *
 * Lines with no usable amount are KEPT — the validator flags those. Only
 * numerically-zero lines are dropped.
 */
export function partitionZeroLines(rows, resolve = resolveLineAmount) {
  const kept = [];
  let zeroCount = 0;

  rows.forEach((row) => {
    if (resolve(row) === 0) {
      zeroCount++;
      return;
    }
    kept.push(row);
  });

  return { kept, zeroCount };
}
